import {
    GoogleAuthProvider,
    OAuthProvider,
    linkWithPopup,
    signInWithPopup,
    unlink,
    User as FirebaseUser,
} from 'firebase/auth';
import {auth} from '../firebaseApp.ts';

export const SSO_PROVIDER_IDS = ['google.com', 'microsoft.com'] as const;
export type SsoProviderId = typeof SSO_PROVIDER_IDS[number];

export interface SSOProfile {
    email: string;
    first_name: string;
    last_name: string;
}

export interface SsoProviderStatus {
    id: SsoProviderId;
    label: string;
    linked: boolean;
    canUnlink: boolean;
}

export function isFromSsoProvider(user: FirebaseUser): boolean {
    return user.providerData.some((provider) => SSO_PROVIDER_IDS.includes(provider.providerId as SsoProviderId));
}

export function hasPasswordProvider(user: FirebaseUser): boolean {
    return user.providerData.some((provider) => provider.providerId === 'password');
}

export function isSsoProviderLinked(user: FirebaseUser, providerId: SsoProviderId): boolean {
    return user.providerData.some((provider) => provider.providerId === providerId);
}

export function canUnlinkSsoProvider(user: FirebaseUser, providerId: SsoProviderId): boolean {
    if (!isSsoProviderLinked(user, providerId)) {
        return false;
    }

    if (hasPasswordProvider(user)) {
        return true;
    }

    const linkedSsoCount = user.providerData.filter((provider) =>
        SSO_PROVIDER_IDS.includes(provider.providerId as SsoProviderId)
    ).length;

    return linkedSsoCount > 1;
}

export function getSsoProviderStatuses(user: FirebaseUser): SsoProviderStatus[] {
    return [
        {
            id: 'google.com',
            label: 'Google',
            linked: isSsoProviderLinked(user, 'google.com'),
            canUnlink: canUnlinkSsoProvider(user, 'google.com'),
        },
        {
            id: 'microsoft.com',
            label: 'Microsoft',
            linked: isSsoProviderLinked(user, 'microsoft.com'),
            canUnlink: canUnlinkSsoProvider(user, 'microsoft.com'),
        },
    ];
}

export function getProfileFromFirebaseUser(user: FirebaseUser): SSOProfile {
    const email = user.email ?? '';
    const displayName = user.displayName?.trim() ?? '';
    const nameParts = displayName.split(/\s+/).filter(Boolean);

    return {
        email,
        first_name: nameParts[0] ?? '',
        last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : '',
    };
}

function createMicrosoftProvider(): OAuthProvider {
    const provider = new OAuthProvider('microsoft.com');
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({
        tenant: 'consumers',
        prompt: 'select_account',
    });
    return provider;
}

function assertCurrentUser(): FirebaseUser {
    if (!auth.currentUser) {
        throw new Error('You must be signed in to manage SSO providers');
    }
    return auth.currentUser;
}

async function linkSsoProvider(provider: GoogleAuthProvider | OAuthProvider): Promise<FirebaseUser> {
    const currentUser = assertCurrentUser();
    const result = await linkWithPopup(currentUser, provider);
    return result.user;
}

export function linkGoogleAccount(): Promise<FirebaseUser> {
    return linkSsoProvider(new GoogleAuthProvider());
}

export function linkMicrosoftAccount(): Promise<FirebaseUser> {
    return linkSsoProvider(createMicrosoftProvider());
}

export async function unlinkSsoProvider(providerId: SsoProviderId): Promise<FirebaseUser> {
    const currentUser = assertCurrentUser();

    if (!canUnlinkSsoProvider(currentUser, providerId)) {
        throw new Error('Je kunt deze inlogmethode niet loskoppelen omdat het je enige inlogmethode is.');
    }

    return unlink(currentUser, providerId);
}

export async function signInWithGoogle(): Promise<FirebaseUser> {
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    return result.user;
}

export async function signInWithMicrosoft(): Promise<FirebaseUser> {
    const result_1 = await signInWithPopup(auth, createMicrosoftProvider());
    return result_1.user;
}
